// Arcade phase machine: pilot-first wave survival, run as a pure World→World
// transform after the tick pipeline commits (see tick/index.ts). Owns wave
// spawning, wave-clear detection, and player death → lives/respawn/game-over.
// No-op unless `world.config.format === "arcade"`. See docs/arcade-mode-plan.md.

import { nextRange } from "~/engine/rng";
import { bakeCaps, pilotMods, rollOffer } from "~/world/augments";
import { rollShip } from "~/world/factory";
import { pilotStart } from "~/world/init";
import {
  activeTeams,
  BASE_MAX_HP,
  HANDICAP_ADAPT_MAX,
  HANDICAP_CLEAN_STEP,
  HANDICAP_DEATH_STEP,
  MAX_ENEMY_SHIPS,
  SPAWN_INVULN_GENS,
  WING_MAX,
  WING_RESPAWN_CD,
} from "~/world/tuning";
import {
  baseByName,
  type LightCycle,
  MAX_LEVEL,
  type RunConfig,
  type RunState,
  type WaveConfig,
  type WaveState,
  type World,
} from "~/world/types";

/** Commit a run-state patch (and optionally a new wave slice) onto the world. */
const withRun = (
  world: World,
  r: RunState,
  patch: Partial<RunState> = {},
): World => ({ ...world, run: { ...r, ...patch } });

/** Commit a wave-director patch, leaving the rest of the run untouched. */
const withWaves = (
  world: World,
  r: RunState,
  w: WaveState,
  patch: Partial<WaveState>,
): World => ({ ...world, run: { ...r, waves: { ...w, ...patch } } });

// Half-width of the square a spawned ship scatters in around its base (cells).
const SPAWN_SPREAD = 7;

/** Alive ships whose team is in `teams`. */
const countTeams = (
  ships: readonly LightCycle[],
  teams: ReadonlySet<string>,
): number => ships.reduce((n, s) => (teams.has(s.colorName) ? n + 1 : n), 0);

/** Append one ship rolled for `color` at level `lvl`, docked at that base. */
function spawnAt(
  world: World,
  color: string,
  lvl: number,
  archetype?: LightCycle["archetype"],
  invuln = 0,
): { world: World; id: number } {
  const id = world.ships.nextId;
  const [ship, s1] = rollShip(
    world.seed,
    id,
    0,
    0,
    lvl,
    color,
    archetype,
    activeTeams(world.config),
  );
  const base = baseByName.get(color);
  const [ox, s2] = nextRange(s1, -SPAWN_SPREAD, SPAWN_SPREAD);
  const [oy, seed] = nextRange(s2, -SPAWN_SPREAD, SPAWN_SPREAD);
  const spot = base
    ? { ...ship, x: base.x + ox, y: base.y + oy, invulnTime: invuln }
    : { ...ship, invulnTime: invuln };
  // Player-team spawns inherit the run's hull/plating augments (fleet-wide:
  // pilot, respawns, mustered escorts). Empty stack = identity, so this is a
  // no-op until a pick lands. Offense augments apply at the pilot's per-shot
  // read sites, not here.
  const mods =
    world.run && color === world.config.run?.playerTeam
      ? pilotMods(world.run.augments)
      : null;
  const placed = mods
    ? {
        ...spot,
        ...bakeCaps(mods, spot),
        hp: Math.round(spot.hp * mods.hpMul),
        shield: Math.round(spot.shield * mods.shieldMul),
      }
    : spot;
  return {
    world: {
      ...world,
      seed,
      ships: { items: [...world.ships.items, placed], nextId: id + 1 },
    },
    id,
  };
}

/** Spawn `count` enemies split round-robin across the enemy teams. */
function spawnWave(
  world: World,
  cfg: RunConfig,
  count: number,
  maxLevel: number,
): World {
  let next = world;
  for (let i = 0; i < count; i++) {
    const team = cfg.enemyTeams[i % cfg.enemyTeams.length];
    // Roll a level in [1, maxLevel] off the world seed (deterministic).
    const [lvlF, seed] = nextRange(next.seed, 1, maxLevel + 0.999);
    next = spawnAt({ ...next, seed }, team, Math.floor(lvlF)).world;
  }
  return next;
}

/**
 * Respawn the player at `lvl` (with i-frames) and hand control back. In the
 * arena that is their home base; on a stage there is no base to launch from, so
 * they come back in the live window exactly where the stage started them —
 * without this, a death mid-stage put the pilot back at a base the stage flew
 * past long ago, off the field entirely.
 */
function respawnPlayer(world: World, cfg: RunConfig, lvl: number): World {
  const { world: next, id } = spawnAt(
    world,
    cfg.playerTeam,
    lvl,
    cfg.playerArchetype,
    SPAWN_INVULN_GENS,
  );
  const start = pilotStart(world.config, cfg.playerTeam, world.scrollY);
  return {
    ...next,
    controlledShipId: id,
    ships: {
      ...next.ships,
      items: next.ships.items.map((s) =>
        s.id === id ? { ...s, ...start } : s,
      ),
    },
  };
}

/** True while the controlled ship is still on the field. */
const playerAlive = (world: World): boolean =>
  world.controlledShipId !== null &&
  world.ships.items.some((s) => s.id === world.controlledShipId);

/** Burn a life; respawn the pilot, or latch game-over when lives run out. */
function loseLife(world: World, a: RunState, cfg: RunConfig): World {
  const lives = a.lives - 1;
  if (lives <= 0) return withRun(world, a, { lives: 0, over: true });
  // Respawn at the rank the pilot had reached — dying costs a life, not progress —
  // and nudge the adaptive handicap up (and mark the wave wounded). No wave
  // director (a scroll stage) means no adaptive curve to nudge.
  const w = a.waves;
  const waves = w
    ? {
        ...w,
        adapt: Math.min(HANDICAP_ADAPT_MAX, w.adapt + HANDICAP_DEATH_STEP),
        woundedWave: true,
      }
    : null;
  return withRun(respawnPlayer(world, cfg, a.playerLevel), a, { lives, waves });
}

/**
 * Fight-phase wave machine: muster the current wave when the field is clear,
 * otherwise trickle the reserve in and advance the wave once the last enemy
 * falls. Kills are banked where ships die (see `killShip`), not counted here.
 */
function advanceWave(
  world: World,
  a: RunState,
  w: WaveState,
  waves: WaveConfig,
  cfg: RunConfig,
  enemyCount: number,
): World {
  // Muster a fresh wave: field clear of enemies AND none held in reserve AND no
  // augment offer pending (the wave-clear pick freezes progression until the
  // player chooses). Spawn up to the on-field budget; the rest wait in `pending`
  // and trickle in below as enemies die, so late waves stay dense.
  if (
    w.waveRemaining === 0 &&
    w.pending === 0 &&
    a.offer === null &&
    enemyCount === 0
  ) {
    const { count, maxLevel } = waves.spawn(w.wave);
    const now = Math.min(count, MAX_ENEMY_SHIPS);
    const spawned = spawnWave(world, cfg, now, maxLevel);
    return withWaves(spawned, a, w, {
      waveRemaining: now,
      pending: count - now,
      waveMaxLevel: maxLevel,
    });
  }
  // Trickle: refill open slots from the reserve as the front thins.
  let next = world;
  let alive = enemyCount;
  let pending = w.pending;
  if (pending > 0) {
    const room = Math.min(pending, Math.max(0, MAX_ENEMY_SHIPS - enemyCount));
    if (room > 0) {
      next = spawnWave(world, cfg, room, w.waveMaxLevel);
      alive = enemyCount + room;
      pending = pending - room;
    }
  }

  if (alive === 0 && pending === 0 && w.waveRemaining > 0) {
    // Wave cleared → advance and offer an augment. The offer freezes the next
    // muster (see the gate above) until the player picks. A clean clear (no
    // death this wave) eases the adaptive handicap back toward the base.
    const adapt = w.woundedWave
      ? w.adapt
      : Math.max(0, w.adapt - HANDICAP_CLEAN_STEP);
    const { offer, seed } = rollOffer(next.seed, a.augments);
    return {
      ...next,
      seed,
      // The lull between waves is a yard-crew moment: whatever the wave chipped
      // off the home base is patched back to full before the next muster.
      baseHp: { ...next.baseHp, [cfg.playerTeam]: BASE_MAX_HP },
      run: {
        ...a,
        offer,
        waves: {
          ...w,
          wave: w.wave + 1,
          waveRemaining: 0,
          pending: 0,
          adapt,
          woundedWave: false,
        },
      },
    };
  }
  return {
    ...next,
    run: { ...a, waves: { ...w, waveRemaining: alive, pending } },
  };
}

// Roll one escort drone at the player base and flag it a droneShip (a small,
// short-firing AI wingman). Reuses spawnAt so it inherits the fleet's hull/
// plating augments and the base-spawn spread.
const spawnWingDrone = (world: World, cfg: RunConfig, level: number): World => {
  const { world: w2, id } = spawnAt(
    world,
    cfg.playerTeam,
    level,
    "scout",
    SPAWN_INVULN_GENS,
  );
  return {
    ...w2,
    ships: {
      ...w2.ships,
      items: w2.ships.items.map((s) =>
        s.id === id ? { ...s, droneShip: true } : s,
      ),
    },
  };
};

// Keep the escort wing (Wing augment) staffed: hold up to WING_MAX drones at the
// pilot's side, respawning one on a cooldown when the count drops. Stacks past
// the cap level the drones up instead of adding more. No-op without the augment.
const maintainWing = (world: World, cfg: RunConfig): World => {
  const a = world.run;
  if (!a) return world;
  const wing = pilotMods(a.augments).wingSize;
  if (wing <= 0) return world;
  const target = Math.min(wing, WING_MAX);
  const alive = world.ships.items.filter(
    (s) =>
      s.droneShip &&
      s.colorName === cfg.playerTeam &&
      s.id !== world.controlledShipId,
  ).length;
  if (alive >= target) return world;
  if (a.wingCd > 0) return withRun(world, a, { wingCd: a.wingCd - 1 });
  const level = Math.min(MAX_LEVEL, 1 + Math.max(0, wing - WING_MAX));
  const spawned = spawnWingDrone(world, cfg, level);
  return withRun(spawned, a, { wingCd: WING_RESPAWN_CD });
};

export function arcadeStep(world: World): World {
  const a = world.run;
  const cfg = world.config.run;
  if (!a || !cfg || a.over) return world;

  if (!playerAlive(world)) return maintainWing(loseLife(world, a, cfg), cfg);

  // Stash the live pilot's rank so a respawn can restore it (see loseLife).
  const me = world.ships.items.find((s) => s.id === world.controlledShipId);
  const a2 =
    me && me.level !== a.playerLevel ? { ...a, playerLevel: me.level } : a;

  // No wave director (a scroll stage places its own enemies) → the run still
  // keeps its wing and its lives, there is just no wave to advance.
  const w = a2.waves;
  const enemyCount = countTeams(world.ships.items, new Set(cfg.enemyTeams));
  // Intermission (w.phase === "intermission") arrives in Phase 2.
  const stepped =
    w && cfg.waves && w.phase === "fight"
      ? advanceWave(world, a2, w, cfg.waves, cfg, enemyCount)
      : { ...world, run: a2 };
  return maintainWing(stepped, cfg);
}
